import { Component, OnInit, OnDestroy, TemplateRef, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import _ from 'lodash';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { DrawerModule } from 'primeng/drawer';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Observable, of, Subscription } from 'rxjs';
import { interval } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IAttachment, IFile, IUser, ResponseJson } from '../../../../../@core/models/model';
import { AuthenticationService } from '../../../../../@core/services/authentication.service';
import { ToastService } from '../../../../../@core/services/toast.service';
import { DataTableComponent } from '../../../../../@shared/components/data-table/data-table.component';
import { SearchBarComponent } from '../../../../../@shared/components/search-bar/search-bar.component';
import { TableColumn, TableConfig } from '../../../../../@shared/models/table.models';
import { UserService } from '../../../../services/user.service';
import { ClauseLibraryService, IClause, IClauseBundle, CLAUSE_SOURCE, CLAUSE_STATUS, CLAUSE_METADATA_STATUS, ClauseSortBy, ClauseSortOrder, IClauseMetadataAuditItem } from '../../../../services/clause-library.service';
import { SOCKET_EVENTS, SocketService } from '../../../../../@core/services/socket.service';
import { CLAUSE_LIBRARY_TABLE_COLUMNS, CLAUSE_LIBRARY_TABLE_CONFIG } from './clause-library-table.config';
import { ButtonComponent } from '../../../../../@core/components/ui/button/button.component';
import { PopoverComponent } from '../../../../../@core/components/ui/popover/popover.component';
import { MultiSelectComponent } from '../../../../../@core/components/ui/multi-select';
import { SelectOption } from '../../../../../@core/components/ui/select';
import { DatepickerComponent } from '../../../../../@core/components/ui/datepicker';
import { UserAvatarPipe } from 'src/app/@shared/pipes/user-avatar-pipe';
import { CLAUSE_LIBRARY } from 'src/app/common/svgs';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { FileSelectionComponent } from '../../../../../@shared/components/file-selection/file-selection.component';
import { FileDropComponent } from '../../../../../@shared/components/file-drop/file-drop.component';
import { FileDropTypes } from '../../../../../@shared/components/file-drop/file-drop.types';
import saveAs from 'file-saver';
import {
    FileSelectionConfig,
    FileSelectionChangeEvent,
    FileSelectionSubmitEvent,
    FileItem,
    TabType,
    FetchFunction,
    DropdownOption
} from '../../../../../@shared/components/file-selection/file-selection.model';
import { KnowledgeBaseService } from '../../../../services/knowledgebase.service';
import { ProjectService } from '../../../../services/project.service';
import { RepositoryService } from '../../../../services/repository.service';
import { ContractService } from '../../../../services/contracts.service';
import { IContract, KNOWLEDGE_SCOPE } from '../../../../models/contract-management.model';
import { ClauseBundleComponent } from '../clause-bundle/clause-bundle.component';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { ClauseBundleService, IClauseBundleWithClauses } from 'src/app/contract-management/services/clause-bundle.service';
import { AttachmentService } from '../../../../services/attachment.service';
import { MultiFormatPreviewComponent } from '../../../../../@shared/components/multi-format-preview/multi-format-preview.component';
import { BlobLoaderComponent } from 'src/app/@shared/components/blob-loader/blob-loader.component';
import { CheckboxComponent } from 'src/app/@core/components/ui/checkbox';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { TextCasePipe } from 'src/app/@shared/pipes/textCase.pipe';
import { FormatTextPipe } from 'src/app/@shared/pipes/formated-text.pipe';
import { LottieAnimationComponent } from '../../../../../@shared/components/lottie-animation/lottie-animation.component';
import { MenuComponent, CustomMenuItem } from '../../../../../@core/components/ui/menu/menu.component';
import { DeleteConfirmationComponent } from '../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display/user-name-display.component';
import { THREAD_STREAMING_STATUS } from 'src/app/features/general-agent/models/model';
import { CreditsOverviewComponent } from "src/app/features/credits/components/credits-overview/credits-overview.component";
import { UserCreditsStateService } from "src/app/features/credits/services/user-credits-state.service";
import { UserCreditsService } from "src/app/features/credits/services/user-credits.service";

export enum LIBRARY_TAB {
    CLAUSE = 'CLAUSE',
    CLAUSE_BUNDLE = 'CLAUSE_BUNDLE'
}

export enum AddClauseMethod {
    MANUAL_DRAFT = 'MANUAL_DRAFT',
    UPLOAD_CONTRACT = 'UPLOAD_CONTRACT',
    EXCEL_IMPORT = 'EXCEL_IMPORT',
    INTEGRATIONS = 'INTEGRATIONS'
}

export enum ExcelUploadFileStatus {
    PENDING = 'PENDING',
    UPLOADING = 'UPLOADING',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED'
}

@Component({
    selector: 'app-clause-library',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        ButtonModule,
        DialogComponent,
        DrawerModule,
        ProgressSpinnerModule,
        ConfirmDialogModule,
        SearchBarComponent,
        DataTableComponent,
        ButtonComponent,
        PopoverComponent,
        MultiSelectComponent,
        DatepickerComponent,
        UserAvatarPipe,
        IconComponent,
        FileSelectionComponent,
        FileDropComponent,
        ClauseBundleComponent,
        TabsModule,
        TooltipModule,
        BlobLoaderComponent,
        CheckboxComponent,
        SanitizeHtmlPipe,
        TextCasePipe,
        FormatTextPipe,
        LottieAnimationComponent,
        MenuComponent,
        DeleteConfirmationComponent,
        MultiFormatPreviewComponent,
        UserNameDisplayComponent,
        CreditsOverviewComponent
    ],
    providers: [ConfirmationService],
    templateUrl: './clause-library.component.html',
    styleUrl: './clause-library.component.scss'
})
export class ClauseLibraryComponent implements OnInit, OnDestroy {
    private routerEventsSub: Subscription | null = null;
    private clauseExtractionSubscription?: Subscription;
    private clauseTextResultSubscription?: Subscription;

    organization: string = '';

    // Enums for template access
    LIBRARY_TAB = LIBRARY_TAB;
    CLAUSE_SOURCE = CLAUSE_SOURCE;
    CLAUSE_STATUS = CLAUSE_STATUS;
    CLAUSE_METADATA_STATUS = CLAUSE_METADATA_STATUS;
    ClauseSortBy = ClauseSortBy;
    AddClauseMethod = AddClauseMethod;
    ExcelUploadFileStatus = ExcelUploadFileStatus;


    showAddClauseDialog: boolean = false;
    selectedAddClauseMethod: AddClauseMethod | null = null;
    showMethodSelectionView: boolean = true;
    addClauseSelectedBundleIds: string[] = [];
    showAddBundleDialog: boolean = false;
    addBundleForm = new FormGroup({
        name: new FormControl('', [Validators.required]),
    });
    get addBundleFormControls() {
        return { name: this.addBundleForm.controls['name'] };
    }
    manualDraftClauseName: string = '';
    manualDraftClauseText: string = '';
    isClauseTextExpanded: boolean = false;
    /** True to show the raw editable textarea for clause text; false shows the rendered markdown preview. */
    isClauseTextRawEdit: boolean = false;
    showPromptForClauseDialog: boolean = false;
    promptForClauseText: string = '';
    isPromptForEditMode: boolean = false;
    generateClauseTextLoading: boolean = false;
    rephraseClauseLoading: boolean = false;
    /** threadId of the in-flight generate/rephrase request for the manual draft or edit dialog target (whichever fired last). */
    pendingClauseTextThreadId: string | null = null;
    pendingClauseTextTarget: 'draft' | 'edit' | null = null;
    dots$ = interval(800).pipe(map(i => '.'.repeat(i % 4)));

    // Active tab
    activeTab: LIBRARY_TAB = LIBRARY_TAB.CLAUSE;

    // Main table properties
    searchValue: string = '';
    clausesLoading: boolean = false;
    clausesData: IClause[] = [];
    skip: number = 0;
    limit: number = 20;
    totalRecords: number = 0;

    // Selection
    selectedClauses: IClause[] = [];
    selectedClauseIds: string[] = [];

    // Template References for data table
    @ViewChild('clauseBundleTemplateRef') clauseBundleTemplateRef!: TemplateRef<any>;
    @ViewChild('sourceTemplateRef') sourceTemplateRef!: TemplateRef<any>;
    @ViewChild('statusTemplateRef') statusTemplateRef!: TemplateRef<any>;
    @ViewChild('createdByTemplateRef') createdByTemplateRef!: TemplateRef<any>;
    @ViewChild('createdAtTemplateRef') createdAtTemplateRef!: TemplateRef<any>;
    @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;
    @ViewChild('dataTable') dataTable!: DataTableComponent;

    // Table Configuration
    clauseTableColumns: TableColumn[] = [...CLAUSE_LIBRARY_TABLE_COLUMNS];
    clauseTableConfig: TableConfig = { ...CLAUSE_LIBRARY_TABLE_CONFIG };

    // Filter properties
    filterDialog: boolean = false;
    sourceOptions: { name: string; value: CLAUSE_SOURCE }[] = [
        { name: 'Manual entry', value: CLAUSE_SOURCE.MANUAL },
        { name: 'Excel', value: CLAUSE_SOURCE.EXCEL },
        { name: 'Extracted', value: CLAUSE_SOURCE.EXTRACTED }
    ];
    statusOptions: { name: string; value: CLAUSE_STATUS }[] = [
        { name: 'Approved', value: CLAUSE_STATUS.APPROVED },
        { name: 'Rejected', value: CLAUSE_STATUS.REJECTED },
        { name: 'Pending', value: CLAUSE_STATUS.PENDING }
    ];
    selectedSources: CLAUSE_SOURCE[] = [];

    selectedCreators: string[] = [];
    selectedClauseBundles: string[] = [];
    selectedDateRange: Date[] | null = null;
    selectedDateChip: 'today' | 'last7days' | 'last30days' | null = null;
    minUsageCount: number | null = null;
    maxUsageCount: number | null = null;
    clauseSortBy: ClauseSortBy = ClauseSortBy.CreatedAt;
    clauseSortOrder: ClauseSortOrder = -1;
    userOptions: { label: string; value: string }[] = [];
    users: IUser[] = [];
    isLoadingUsers: boolean = false;

    // Clause bundles
    clauseBundles: IClauseBundle[] = [];
    clauseBundleOptions: SelectOption[] = [];

    // Unified View/Edit Clause Dialog (also used for Add manual)
    viewEditDialog: boolean = false;
    isEditMode: boolean = false;
    /** True when dialog is open for adding a new clause (manual draft), false when viewing/editing existing */
    isAddMode: boolean = false;
    currentClause: Partial<IClause> & { prompt?: string } = {};
    currentClauseBundleIds: string[] = [];
    saveClauseLoading: boolean = false;

    showLinkBundleDialog: boolean = false;
    bulkLinkSearchValue: string = '';
    selectedBundleIdsForBulkLink: string[] = [];
    bulkLinkingToBundles: boolean = false;
    showCreateBundleInBulkLinkDialog: boolean = false;
    createBundleFromBulkLinkForm = new FormGroup({
        name: new FormControl('', [Validators.required]),
    });
    get createBundleFromBulkLinkFormControls() {
        return { name: this.createBundleFromBulkLinkForm.controls['name'] };
    }
    createBundleFromBulkLinkLoading: boolean = false;

    // Delete confirmation
    showDeleteConfirmDialog: boolean = false;
    itemsToDeleteCount: number = 0;

    // Bulk edit dialog
    showBulkEditDialog: boolean = false;
    editingClauses: IClause[] = [];
    originalClauses: IClause[] = [];
    currentEditIndex: number = 0;
    savedClauses: Set<number> = new Set();
    totalEditingCount: number = 0;
    isBulkEditPreviousDisabled: boolean = true;
    isBulkEditNextDisabled: boolean = true;
    isBulkEditSaveDisabled: boolean = true;
    isBulkEditSaving: boolean = false;
    bulkEditGenerateLoading: boolean = false;
    bulkEditRephraseLoading: boolean = false;
    /** threadId of the in-flight generate/rephrase request for the currently active bulk-edit row. */
    pendingBulkClauseTextThreadId: string | null = null;
    pendingBulkClauseTextEditIndex: number | null = null;
    isPromptForBulkEditMode: boolean = false;
    bulkEditClauseTextExpanded: boolean = false;

    // Icons
    ICON_SOURCE = IconSourceEnum;
    CLAUSE_LIBRARY_ICONS = CLAUSE_LIBRARY;

    @ViewChild('mobileMenu') mobileMenu!: any;
    @ViewChild('sortFilterPopover') sortFilterPopover!: any;
    @ViewChild('clauseBundleHeaderTemplate') clauseBundleHeaderTemplate!: TemplateRef<any>;
    @ViewChild(ClauseBundleComponent) clauseBundleRef?: ClauseBundleComponent;

    // File selection for upload contract
    fileSelectionVisible = signal<boolean>(false);
    uploadContractSelectedBundleIds: string[] = [];

    // Clause extraction status dialog (shown after submitting files for extraction)
    showClauseExtractionDialog: boolean = false;
    clauseExtractionAnimationData: any = null;

    // History sidebar (uploaded files / clause extraction audit)
    openHistorySidebar: boolean = false;
    uploadedFilesAudit: IClauseMetadataAuditItem[] = [];
    showHistoryFilePreview: boolean = false;
    selectedHistoryFileAttachment: IAttachment | null = null;
    selectedHistoryDocumentId: string | null = null;
    isLoadingHistoryPreview: boolean = false;

    // Excel upload dialog (Upload document)
    showExcelUploadDialog: boolean = false;
    excelUploadSelectedBundleIds: string[] = [];
    excelUploadFiles: { file: File; status: ExcelUploadFileStatus }[] = [];
    excelUploadSubmitting: boolean = false;
    EXCEL_UPLOAD_MAX_SIZE_MB = 90 * 1000 * 1000;
    EXCEL_UPLOAD_ALLOWED_TYPES = [FileDropTypes.XLSX, '.xls'];
    knowledgeBases: any[] = [];
    projects: any[] = [];
    fileSelectionConfig = signal<FileSelectionConfig>({
        title: 'Upload files',
        localUploadMaxFileSize: 50 * 1000 * 1000, // 50MB limit for individual locally uploaded files
        allowedFileTypes: [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC],
        dialogWidth: '60vw',
        dialogHeight: '90vh',
        submitButtonLabel: 'Upload',
        cancelButtonLabel: 'Cancel',
        searchPlaceholder: 'Search with name',
        defaultPageSize: 10,
        rowsPerPageOptions: [10, 20, 50],
        tabs: [
            {
                id: 'workspace',
                label: 'Workspace knowledge',
                type: TabType.SELECT
            },
            {
                id: 'project',
                label: 'Project knowledge',
                type: TabType.SELECT
            },
            {
                id: 'documents',
                label: 'Documents',
                type: TabType.SELECT
            },
            {
                id: 'upload',
                label: 'Files from local',
                type: TabType.UPLOAD
            }
        ]
    });
    clauseBundleDropdownOptions = signal<DropdownOption[]>([]);

    constructor(
        private clauseLibraryService: ClauseLibraryService,
        private toast: ToastService,
        private userService: UserService,
        private confirmationService: ConfirmationService,
        private authService: AuthenticationService,
        private knowledgeBaseService: KnowledgeBaseService,
        private projectService: ProjectService,
        private repositoryService: RepositoryService,
        private contractService: ContractService,
        private router: Router,
        private readonly route: ActivatedRoute,
        private clauseBundleService: ClauseBundleService,
        private socketService: SocketService,
        private attachmentService: AttachmentService,
        public creditsState: UserCreditsStateService,
        private userCreditsService: UserCreditsService,
    ) {
        this.authService.currentUser.subscribe(res => {
            this.organization = res?.organization as string;
        });
    }

    ngOnInit() {
        this.refreshContent();
        this.setupClauseExtractionSocket();
        this.setupClauseTextResultSocket();
        this.routerEventsSub = this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                if (!this.isChildRouteActive()) {
                    this.refreshContent();
                    this.clearSelections();
                }
            });

        this.route.queryParams.subscribe(params => {
            if (params['openAction'] === 'add-clause') {
                this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
                setTimeout(() => this.addClause(), 300);
            }
        });
    }

    ngOnDestroy() {
        this.routerEventsSub?.unsubscribe();
        this.clauseExtractionSubscription?.unsubscribe();
        this.clauseTextResultSubscription?.unsubscribe();
    }

    private setupClauseExtractionSocket() {
        this.clauseExtractionSubscription = this.socketService
            .on<{ organization: string; clauseMetadataId: string; status: CLAUSE_METADATA_STATUS; metadata?: { attachment?: { filename?: string } } }>(
                SOCKET_EVENTS.CLAUSE_EXTRACTION_STATUS_CHANGED
            )
            .subscribe((data) => {
                const payload = data[0];
                if (!payload || payload.organization !== this.organization) return;
                this.refreshUploadedFilesAudit();
                const filename = payload.metadata?.attachment?.filename?.trim();
                if (payload.status === CLAUSE_METADATA_STATUS.COMPLETED) {
                    const msg = filename
                        ? `Clauses successfully extracted from ${filename} and added to review`
                        : 'Clauses successfully extracted and added to review';
                    this.toast.success(msg);
                    this.showClauseExtractionDialog = false;
                } else if (payload.status === CLAUSE_METADATA_STATUS.FAILED) {
                    const msg = filename
                        ? `We couldn't process your document (${filename}). Please retry or upload a different file`
                        : "We couldn't process your document. Please retry or upload a different file";
                    this.toast.error(msg);
                    this.showClauseExtractionDialog = false;
                }
            });
    }

    /**
     * Routes async generate/rephrase clause-text results (streamed from the Python
     * ClauseTextAgent via RabbitMQ -> Node -> socket) back to whichever UI target
     * (manual draft, edit dialog, or the active bulk-edit row) fired the matching threadId.
     */
    private setupClauseTextResultSocket() {
        this.clauseTextResultSubscription = this.socketService
            .on<{ status: string; content: string; threadId: string }>(SOCKET_EVENTS.CLAUSE_TEXT_RESULT)
            .subscribe((res) => {
                const payload = res[0];
                if (!payload) return;
                const { status, content, threadId } = payload;
                const isDone = status === THREAD_STREAMING_STATUS.COMPLETED;
                const isError = status === THREAD_STREAMING_STATUS.ERROR;
                if (!isDone && !isError) return; // ignore IN_PROGRESS chunks — only the final text is used

                if (threadId === this.pendingClauseTextThreadId) {
                    this.generateClauseTextLoading = false;
                    this.rephraseClauseLoading = false;
                    const target = this.pendingClauseTextTarget;
                    this.pendingClauseTextThreadId = null;
                    this.pendingClauseTextTarget = null;
                    if (isDone) {
                        if (target === 'edit') {
                            this.currentClause = { ...this.currentClause, text: content };
                            this.isClauseTextRawEdit = false;
                        } else {
                            this.manualDraftClauseText = content;
                        }
                        this.toast.success('Clause text updated');
                    } else {
                        this.toast.error('Something went wrong, please try again');
                    }
                } else if (threadId === this.pendingBulkClauseTextThreadId) {
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    const editIndex = this.pendingBulkClauseTextEditIndex;
                    this.pendingBulkClauseTextThreadId = null;
                    this.pendingBulkClauseTextEditIndex = null;
                    if (isDone && editIndex != null && this.editingClauses[editIndex]) {
                        this.editingClauses[editIndex] = { ...this.editingClauses[editIndex], text: content };
                        this.saveClauseInBulkEdit(editIndex);
                        this.toast.success('Clause text updated');
                    } else if (isError) {
                        this.toast.error('Something went wrong, please try again');
                    }
                }
                // else: stale/foreign threadId (e.g. an abandoned or cancelled earlier request) — ignore
            });
    }

    private refreshUploadedFilesAudit() {
        this.clauseLibraryService.getUploadedFilesAudit({ limit: 50, skip: 0 }).subscribe((res) => {
            if (res.success && res.data) {
                this.uploadedFilesAudit = res.data;
            }
        });
    }

    onResumeClauseExtraction(file: IClauseMetadataAuditItem) {
        const fileId = file._id;
        this.userCreditsService.removeExhaustion('CLAUSE_EXTRACTION', fileId).subscribe({
            next: () => {
                this.creditsState.refreshCredits().subscribe();
                this.clauseLibraryService.retryClauseExtraction(fileId).subscribe({
                    next: () => {
                        this.toast.success('Clause extraction resumed');
                        this.refreshUploadedFilesAudit();
                    },
                    error: () => this.toast.error('Failed to resume clause extraction'),
                });
            },
            error: () => this.toast.error('Failed to remove exhaustion record'),
        });
    }

    refreshContent() {
        this.getClauses();
        this.getUsers();
        this.getClauseBundles();
    }


    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            'clauseBundleTemplate': this.clauseBundleTemplateRef,
            'sourceTemplate': this.sourceTemplateRef,
            'statusTemplate': this.statusTemplateRef,
            'createdByTemplate': this.createdByTemplateRef,
            'createdAtTemplate': this.createdAtTemplateRef,
            'actionsTemplate': this.actionsTemplateRef
        };
    }

    // Tab switching
    switchTab(tab: LIBRARY_TAB) {
        this.clearSelections();
        this.activeTab = tab;
        this.refreshContent();
    }

    onTabValueChange(value: string | number | undefined) {
        if (value === LIBRARY_TAB.CLAUSE || value === LIBRARY_TAB.CLAUSE_BUNDLE) {
            this.switchTab(value);
        }
    }

    // Get clauses
    getClauses(append: boolean = false) {
        this.clausesLoading = true;
        this.clauseTableConfig = { ...this.clauseTableConfig, loading: true };

        const filters = {
            searchValue: this.searchValue,
            status: CLAUSE_STATUS.APPROVED,
            skip: this.skip,
            limit: this.limit,
            source: this.selectedSources.length > 0 ? this.selectedSources : undefined,
            createdBy: this.selectedCreators.length > 0 ? this.selectedCreators : undefined,
            clauseBundle: this.selectedClauseBundles.length > 0 ? this.selectedClauseBundles : undefined,
            createdAt: this.selectedDateRange && this.selectedDateRange.length === 2 ? {
                startDate: this.selectedDateRange[0],
                endDate: this.selectedDateRange[1]
            } : undefined,
            minUsageCount: this.minUsageCount ?? undefined,
            maxUsageCount: this.maxUsageCount ?? undefined,
            sortBy: this.clauseSortBy,
            sortOrder: this.clauseSortOrder
        };

        this.clauseLibraryService.getClauses(filters).subscribe(res => {
            this.clausesLoading = false;
            this.clauseTableConfig = { ...this.clauseTableConfig, loading: false };

            if (res.success) {
                if (append) {
                    this.clausesData = [...this.clausesData, ...res.data.clauses];
                } else {
                    this.clausesData = res.data.clauses;
                }
                this.totalRecords = res.data.total;
            }
        });
    }

    // Pagination
    onPageChange(event: any) {
        this.skip = event.first;
        this.limit = event.rows;
        this.getClauses(false);
    }

    setClauseSort(sortBy: ClauseSortBy, sortOrder?: ClauseSortOrder) {
        this.clauseSortBy = sortBy;
        this.clauseSortOrder = sortOrder ?? (sortBy === ClauseSortBy.Name ? 1 : -1);
        this.clausesData = [];
        this.skip = 0;
        this.sortFilterPopover?.hide();
        this.getClauses(false);
    }

    setSortOrder(order: ClauseSortOrder) {
        this.clauseSortOrder = order;
        this.clausesData = [];
        this.skip = 0;
        this.sortFilterPopover?.hide();
        this.getClauses(false);
    }

    // Selection
    onSelectionChange(event: any) {
        this.selectedClauses = event;
        this.selectedClauseIds = event.map((item: IClause) => item._id);
    }

    clearSelections() {
        this.selectedClauses = [];
        this.selectedClauseIds = [];
        this.dataTable?.clearSelection();
    }

    // Search
    searchClauses = _.debounce(() => {
        this.skip = 0;
        this.getClauses();
    }, 300);

    onSearch() {
        this.searchClauses();
    }

    // Filter
    openFilterDialog() {
        this.filterDialog = true;
    }

    applyFilter() {
        this.filterDialog = false;
        this.skip = 0;
        this.getClauses();
    }

    clearFilter() {
        this.selectedSources = [];
        this.selectedCreators = [];
        this.selectedClauseBundles = [];
        this.selectedDateRange = null;
        this.selectedDateChip = null;
        this.minUsageCount = null;
        this.maxUsageCount = null;
        this.filterDialog = false;
        this.skip = 0;
        this.getClauses();
    }

    // Date range selection methods
    selectDateRange(range: 'today' | 'last7days' | 'last30days') {
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today

        let startDate = new Date();

        switch (range) {
            case 'today':
                startDate.setHours(0, 0, 0, 0); // Start of today
                this.selectedDateChip = 'today';
                break;
            case 'last7days':
                startDate.setDate(today.getDate() - 7);
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'last7days';
                break;
            case 'last30days':
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'last30days';
                break;
        }

        this.selectedDateRange = [startDate, today];
    }

    getDateChipClass(range: 'today' | 'last7days' | 'last30days'): string {
        const isSelected = this.selectedDateChip === range;
        return isSelected
            ? 'bg-gray-700 text-white hover:bg-gray-800'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }

    onDateRangeChange() {
        // Clear selected chip when user manually selects dates
        this.selectedDateChip = null;
    }

    // Add clause – open method selection dialog
    addClause() {
        this.selectedAddClauseMethod = null;
        this.showMethodSelectionView = true;
        this.addClauseSelectedBundleIds = [];
        this.manualDraftClauseName = '';
        this.manualDraftClauseText = '';
        this.isClauseTextExpanded = false;
        this.showAddClauseDialog = true;
        this.selectAddClauseMethod(AddClauseMethod.MANUAL_DRAFT)
    }

    closeAddClauseDialog() {
        this.showAddClauseDialog = false;
        this.selectedAddClauseMethod = null;
        this.showMethodSelectionView = true;
    }

    // tabs
    getTabValue(tab: LIBRARY_TAB): string {
        switch (tab) {
            case LIBRARY_TAB.CLAUSE:
                return '0';
            case LIBRARY_TAB.CLAUSE_BUNDLE:
                return '1';
            default:
                return '0';
        }
    }

    selectAddClauseMethod(method: AddClauseMethod) {
        if (method === AddClauseMethod.INTEGRATIONS) return;
        if (method === AddClauseMethod.UPLOAD_CONTRACT) {
            const agentRemaining = this.creditsState.agentRemaining();
            if (agentRemaining <= 0) {
                this.toast.error("You've run out of credits. Request more to continue.");
                return;
            }
            if (agentRemaining < 3) {
                this.toast.warn(`Low credits (${agentRemaining} remaining). This analysis may not complete.`);
            }
        }
        this.selectedAddClauseMethod = method;
    }

    isAddClauseMethodSelected(method: AddClauseMethod): boolean {
        return this.selectedAddClauseMethod === method;
    }

    isAddClauseContinueDisabled(): boolean {
        return !this.selectedAddClauseMethod || this.selectedAddClauseMethod === AddClauseMethod.INTEGRATIONS;
    }

    continueAddClause() {
        if (!this.selectedAddClauseMethod) {
            this.toast.error('Please select how you would like to create the clause');
            return;
        }

        if (this.selectedAddClauseMethod === AddClauseMethod.MANUAL_DRAFT) {
            this.openUnifiedDialogForAddManual();
        } else if (this.selectedAddClauseMethod === AddClauseMethod.UPLOAD_CONTRACT) {
            this.openFileSelectionDialog();
        } else if (this.selectedAddClauseMethod === AddClauseMethod.EXCEL_IMPORT) {
            this.openExcelUploadDialog();
        }
    }

    /** Open the unified clause dialog in add (manual draft) mode; closes Add clause dialog. */
    openUnifiedDialogForAddManual() {
        this.currentClause = {
            name: this.manualDraftClauseName,
            text: this.manualDraftClauseText,
            source: CLAUSE_SOURCE.MANUAL,
            status: CLAUSE_STATUS.APPROVED
        };
        this.currentClauseBundleIds = [...this.addClauseSelectedBundleIds];
        this.isAddMode = true;
        this.isEditMode = true;
        this.isClauseTextRawEdit = false;
        this.viewEditDialog = true;
        this.showAddClauseDialog = false;
    }

    openExcelUploadDialog() {
        this.excelUploadSelectedBundleIds = [...(this.addClauseSelectedBundleIds || [])];
        this.excelUploadFiles = [];
        this.showExcelUploadDialog = true;
    }

    closeExcelUploadDialog() {
        this.showExcelUploadDialog = false;
        this.excelUploadFiles = [];
    }

    downloadClauseSampleExcel() {
        this.clauseLibraryService.downloadSampleExcel().subscribe({
            next: (res) => {
                if (res?.success && res?.data) {
                    const blob = new Blob([new Uint8Array((res.data as any).data)], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
                    });
                    saveAs(blob, 'Clause Library Sample Excel.xlsx');
                }
            },
            error: () => this.toast.error('Failed to download sample sheet')
        });
    }

    onExcelFileUpload(files: File | File[]) {
        const list = Array.isArray(files) ? files : [files];
        const newItems = list.map(file => ({ file, status: ExcelUploadFileStatus.PENDING as ExcelUploadFileStatus }));
        this.excelUploadFiles = [...this.excelUploadFiles, ...newItems];
    }

    onExcelFileRemove(file: File) {
        this.excelUploadFiles = this.excelUploadFiles.filter(item => item.file !== file);
    }

    removeExcelUploadFile(item: { file: File; status: ExcelUploadFileStatus }) {
        this.excelUploadFiles = this.excelUploadFiles.filter(x => x !== item);
    }

    retryExcelUploadFile(item: { file: File; status: ExcelUploadFileStatus }) {
        item.status = ExcelUploadFileStatus.PENDING;
    }

    submitExcelUpload() {
        if (this.excelUploadFiles.length === 0) {
            this.toast.error('Please add at least one file');
            return;
        }
        this.excelUploadSubmitting = true;
        const formData = new FormData();
        formData.append('bundleIds', this.excelUploadSelectedBundleIds.join(','));
        this.excelUploadFiles.forEach(item => {
            formData.append('files', item.file);
        });
        this.clauseLibraryService.uploadExcelClauses(formData).subscribe({
            next: (res) => {
                if (res.success) {
                    if (res.data?.errorFile) {
                        const blob = new Blob([new Uint8Array(res.data.errorFile.data)], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
                        });
                        saveAs(blob, 'Clause Library Errors Excel.xlsx');
                        this.toast.error(res.message);
                    } else {
                        this.toast.success('Clauses uploaded successfully');
                    }
                    this.closeExcelUploadDialog();
                    this.closeAddClauseDialog();
                    this.skip = 0;
                    this.getClauses();
                    this.getClauseBundles();
                } else {
                    this.toast.error(res.message);
                }
            },
            error: () => this.toast.error('Upload failed'),
            complete: () => { this.excelUploadSubmitting = false; }
        });
    }

    formatExcelFileSize(bytes: number): string {
        if (bytes < 1000) return bytes + ' B';
        if (bytes < 1000 * 1000) return (bytes / 1000).toFixed(2) + ' KB';
        return (bytes / (1000 * 1000)).toFixed(2) + ' MB';
    }

    getExcelUploadExistingFiles(): File[] {
        return this.excelUploadFiles.map(item => item.file);
    }

    openFileSelectionDialog() {
        this.uploadContractSelectedBundleIds = [...(this.addClauseSelectedBundleIds || [])];

        // Load knowledge bases and projects
        this.loadKnowledgeBases();
        this.loadProjects();

        // Configure file selection
        this.fileSelectionConfig.set({
            title: 'Upload files',
            localUploadMaxFileSize: 50 * 1000 * 1000,
            showTotalAttachmentsCount: false,
            allowedFileTypes: [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC],
            dialogWidth: '60vw',
            dialogHeight: '90vh',
            submitButtonLabel: 'Upload',
            cancelButtonLabel: 'Cancel',
            searchPlaceholder: 'Search with name',
            defaultPageSize: 10,
            rowsPerPageOptions: [10, 20, 50],
            tabs: [
                {
                    id: 'workspace',
                    label: 'Workspace knowledge',
                    type: TabType.SELECT
                },
                {
                    id: 'project',
                    label: 'Project knowledge',
                    type: TabType.SELECT
                },
                {
                    id: 'documents',
                    label: 'Documents',
                    type: TabType.SELECT
                },
                {
                    id: 'upload',
                    label: 'Files from local',
                    type: TabType.UPLOAD
                }
            ]
        });

        // Show file selection dialog
        this.fileSelectionVisible.set(true);
    }

    loadKnowledgeBases() {
        this.knowledgeBaseService.getKnowledgeBaseStore().subscribe((res) => {
            if (res.success) {
                this.knowledgeBases = res.data.stores || [];
            }
        });
    }

    loadProjects() {
        this.projectService.getProjects(0, 1000).subscribe((res) => {
            if (res.success) {
                this.projects = res.data.projects || [];
            }
        });
    }

    addClauseNewBundle() {
        this.openAddBundleDialog();
    }

    openAddBundleDialog() {
        this.addBundleForm.reset({ name: '' });
        this.showAddBundleDialog = true;
    }

    closeAddBundleDialog() {
        this.showAddBundleDialog = false;
    }

    createBundleAndSelect() {
        const name = this.addBundleForm.value.name?.trim();
        if (!name) return;
        this.clauseBundleService.createBundle(name).subscribe((res) => {
            if (res?.success && res?.data) {
                const newId = (res.data as { _id?: string })._id;
                this.toast.success('Clause bundle created successfully');
                this.showAddBundleDialog = false;
                this.getClauseBundles();
                this.clauseBundleRef?.loadBundles();
                if (newId) {
                    this.addClauseSelectedBundleIds = [...(this.addClauseSelectedBundleIds || []), newId];
                }
            } else {
                this.toast.error('Failed to create clause bundle');
            }
        });
    }

    toggleClauseTextExpanded() {
        this.isClauseTextExpanded = !this.isClauseTextExpanded;
    }

    toggleClauseTextRawEdit() {
        this.isClauseTextRawEdit = !this.isClauseTextRawEdit;
    }

    toggleBulkEditClauseTextExpanded() {
        this.bulkEditClauseTextExpanded = !this.bulkEditClauseTextExpanded;
    }

    rephraseClause() {
        const name = this.manualDraftClauseName?.trim() ?? '';
        const text = this.manualDraftClauseText?.trim();
        if (!text) {
            this.toast.error('Clause text is empty');
            return;
        }
        this.rephraseClauseLoading = true;
        this.clauseLibraryService.rephraseClause(name, text).subscribe({
            next: (res) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingClauseTextThreadId = res.data.threadId;
                    this.pendingClauseTextTarget = 'draft';
                } else {
                    this.rephraseClauseLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text rephrase');
                }
            },
            error: () => {
                this.rephraseClauseLoading = false;
                this.toast.error('Failed to start clause text rephrase');
            }
        });
    }

    openPromptForClauseDialog() {
        const name = (this.viewEditDialog && this.currentClause?.name)
            ? this.currentClause.name.trim()
            : (this.manualDraftClauseName ?? '').trim();
        if (!name) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.isPromptForEditMode = !!(this.viewEditDialog && (this.currentClause?._id || this.isAddMode));
        this.promptForClauseText = '';
        this.showPromptForClauseDialog = true;
    }

    generateClauseTextForEdit() {
        const name = this.currentClause.name?.trim();
        if (!name) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.generateClauseTextLoading = true;
        this.clauseLibraryService.generateClauseText(name, '').subscribe({
            next: (res) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingClauseTextThreadId = res.data.threadId;
                    this.pendingClauseTextTarget = 'edit';
                } else {
                    this.generateClauseTextLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text generation');
                }
            },
            error: () => {
                this.generateClauseTextLoading = false;
                this.toast.error('Failed to start clause text generation');
            }
        });
    }

    rephraseClauseForEdit() {
        const name = this.currentClause.name?.trim() ?? '';
        const text = (this.currentClause.text ?? '').trim();
        if (!text) {
            this.toast.error('Clause text is empty');
            return;
        }
        this.rephraseClauseLoading = true;
        this.clauseLibraryService.rephraseClause(name, text).subscribe({
            next: (res) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingClauseTextThreadId = res.data.threadId;
                    this.pendingClauseTextTarget = 'edit';
                } else {
                    this.rephraseClauseLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text rephrase');
                }
            },
            error: () => {
                this.rephraseClauseLoading = false;
                this.toast.error('Failed to start clause text rephrase');
            }
        });
    }

    closePromptForClauseDialog() {
        this.showPromptForClauseDialog = false;
        this.promptForClauseText = '';
        this.isPromptForEditMode = false;
        this.isPromptForBulkEditMode = false;
    }

    submitPromptForClause() {
        if (this.isPromptForBulkEditMode) {
            const clause = this.editingClauses[this.currentEditIndex];
            const name = (clause?.name ?? '').trim();
            const prompt = (this.promptForClauseText ?? '').trim();
            if (!name) {
                this.toast.error('Please enter clause name first');
                return;
            }
            this.closePromptForClauseDialog();
            const editIndex = this.currentEditIndex;
            this.bulkEditGenerateLoading = true;
            this.clauseLibraryService.generateClauseText(name, prompt).subscribe({
                next: (res: any) => {
                    if (res?.success && res?.data?.threadId) {
                        this.pendingBulkClauseTextThreadId = res.data.threadId;
                        this.pendingBulkClauseTextEditIndex = editIndex;
                    } else {
                        this.bulkEditGenerateLoading = false;
                        this.toast.error(res?.message || 'Failed to start clause text generation');
                    }
                },
                error: () => {
                    this.bulkEditGenerateLoading = false;
                    this.toast.error('Failed to start clause text generation');
                }
            });
            return;
        }
        const isForEdit = this.isPromptForEditMode;
        const name = isForEdit
            ? (this.currentClause.name ?? '').trim()
            : (this.manualDraftClauseName ?? '').trim();
        const prompt = (this.promptForClauseText ?? '').trim();
        if (!name) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.closePromptForClauseDialog();
        this.generateClauseTextLoading = true;
        this.clauseLibraryService.generateClauseText(name, prompt).subscribe({
            next: (res: any) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingClauseTextThreadId = res.data.threadId;
                    this.pendingClauseTextTarget = isForEdit ? 'edit' : 'draft';
                } else {
                    this.generateClauseTextLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text generation');
                }
            },
            error: () => {
                this.generateClauseTextLoading = false;
                this.toast.error('Failed to start clause text generation');
            }
        });
    }

    saveManualDraftClause() {
        if (!this.manualDraftClauseName?.trim()) {
            this.toast.error('Please enter clause name');
            return;
        }
        this.saveClauseLoading = true;
        const name = this.manualDraftClauseName.trim();
        const text = this.manualDraftClauseText?.trim() || ' ';
        const bundleIds = this.addClauseSelectedBundleIds ?? [];
        this.clauseLibraryService.createClause({
            name,
            text,
            source: CLAUSE_SOURCE.MANUAL,
            status: CLAUSE_STATUS.APPROVED,
            ...(bundleIds.length > 0 ? { clauseBundles: bundleIds } : {})
        }).subscribe({
            next: (res) => {
                this.saveClauseLoading = false;
                if (res.success) {
                    this.toast.success('Clause created successfully');
                    this.closeAddClauseDialog();
                    this.skip = 0;
                    this.getClauses();
                    this.getClauseBundles();
                }
            },
            error: () => {
                this.saveClauseLoading = false;
                this.toast.error('Failed to create clause');
            }
        });
    }

    backToAddClauseMethod() {
        this.selectedAddClauseMethod = null;
        this.showMethodSelectionView = true;
    }

    // Edit clause (opens unified dialog in edit mode)
    editClause(clause: IClause) {
        this.isAddMode = false;
        this.isEditMode = true;
        this.isClauseTextRawEdit = false;
        this.generateClauseTextLoading = false;
        this.rephraseClauseLoading = false;
        this.currentClause = { ...clause, prompt: (clause as any).prompt ?? '' };
        this.currentClauseBundleIds = this.getClauseBundleIds(clause);
        this.viewEditDialog = true;
    }

    // Save clause (create or update)
    saveClause() {
        if (!this.currentClause.name?.trim() || !this.currentClause.text?.trim()) {
            this.toast.error('Please fill in all required fields');
            return;
        }

        this.saveClauseLoading = true;

        if (this.isAddMode) {
            // Create new clause (manual draft) and optionally link to bundles
            const name = this.currentClause.name!.trim();
            const text = (this.currentClause.text ?? '').trim() || ' ';
            const bundleIds = this.currentClauseBundleIds ?? [];
            this.clauseLibraryService.createClause({
                name,
                text,
                source: CLAUSE_SOURCE.MANUAL,
                status: CLAUSE_STATUS.APPROVED,
                ...(bundleIds.length > 0 ? { clauseBundles: bundleIds } : {})
            }).subscribe({
                next: (res: any) => {
                    const createdId = res?.data?._id ?? res?._id;
                    if (res?.success && createdId && bundleIds.length > 0) {
                        // Backend links on create when clauseBundles + APPROVED; ensure bundles stay in sync via bulk-link
                        this.clauseLibraryService.bulkLinkClausesToBundles([createdId], bundleIds).subscribe({
                            next: () => {
                                this.saveClauseLoading = false;
                                this.toast.success('Clause created successfully');
                                this.closeUnifiedDialogAndRefresh();
                            },
                            error: () => {
                                this.saveClauseLoading = false;
                                this.toast.success('Clause created successfully');
                                this.closeUnifiedDialogAndRefresh();
                            }
                        });
                    } else {
                        this.saveClauseLoading = false;
                        this.toast.success('Clause created successfully');
                        this.closeUnifiedDialogAndRefresh();
                    }
                },
                error: () => {
                    this.saveClauseLoading = false;
                    this.toast.error('Failed to create clause');
                }
            });
        } else if (this.isEditMode && this.currentClause._id) {
            // Update existing clause
            this.clauseLibraryService.updateClause(this.currentClause._id, {
                name: this.currentClause.name,
                text: this.currentClause.text,
                status: this.currentClause.status,
                clauseBundles: this.currentClauseBundleIds
            }).subscribe((res) => {
                this.saveClauseLoading = false;
                if (res.success) {
                    this.toast.success('Clause updated successfully');
                    this.closeUnifiedDialogAndRefresh();
                }
            }, (error) => {
                this.saveClauseLoading = false;
                this.toast.error('Failed to update clause');
            });
        }
    }

    private closeUnifiedDialogAndRefresh() {
        this.viewEditDialog = false;
        this.isAddMode = false;
        this.skip = 0;
        this.getClauses();
        this.getClauseBundles();
        this.clearSelections();
    }

    /** Handles visibility change from p-dialog. Intercepts close (false) while generating/rephrasing so the dialog never closes before the confirm; avoids close-then-reopen flicker. */
    onViewEditDialogVisibleChange(value: boolean) {
        if (value === false) {
            if (this.generateClauseTextLoading || this.rephraseClauseLoading) {
                this.confirmationService.confirm({
                    message: 'The clause is currently being generated. If you exit now, it will not be created. Are you sure you want to cancel?',
                    header: 'Cancel generation?',
                    acceptLabel: 'Yes',
                    rejectLabel: 'No',
                    accept: () => {
                        this.pendingClauseTextThreadId = null;
                        this.pendingClauseTextTarget = null;
                        this.viewEditDialog = false;
                        this.generateClauseTextLoading = false;
                        this.rephraseClauseLoading = false;
                        this.isClauseTextExpanded = false;
                        this.isAddMode = false;
                        this.currentClause = {};
                    },
                    reject: () => { }
                });
                return;
            }
            this.viewEditDialog = false;
            this.isClauseTextExpanded = false;
            this.isAddMode = false;
            this.generateClauseTextLoading = false;
            this.rephraseClauseLoading = false;
            this.currentClause = {};
        } else {
            this.viewEditDialog = true;
        }
    }

    // View clause (opens unified dialog in view mode)
    viewClauseDetails(clause: IClause) {
        this.isEditMode = false;
        this.isClauseTextRawEdit = false;
        this.currentClause = { ...clause, prompt: (clause as any).prompt ?? '' };
        this.currentClauseBundleIds = this.getClauseBundleIds(clause);
        this.viewEditDialog = true;
    }

    // Link to bundle (single row action): use same flow as bulk link with selection set to this clause
    openLinkBundleDialog(clause: IClause) {
        this.selectedClauseIds = [clause._id];
        this.selectedClauses = [clause];
        this.openBulkLinkBundleDialog();
    }

    // Delete clause
    deleteSingleClause(clause: IClause) {
        this.selectedClauseIds = [clause._id];
        this.itemsToDeleteCount = 1;
        this.showDeleteConfirmDialog = true;
    }

    getClauseMenuOptions(clause: IClause): CustomMenuItem[] {
        return [
            { label: 'Edit', icon: 'pi pi-pencil', value: 'edit', callback: () => this.editClause(clause) },
            { label: 'View', icon: 'pi pi-eye', value: 'view', callback: () => this.viewClauseDetails(clause) },
            { label: 'Link to clause bundle', icon: 'pi pi-link', value: 'link', callback: () => this.openLinkBundleDialog(clause) },
            { label: 'Delete', icon: 'pi pi-trash', value: 'delete', class: 'text-danger', callback: () => this.deleteSingleClause(clause) }
        ];
    }

    bulkDeleteClauses() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause to delete');
            return;
        }
        this.itemsToDeleteCount = this.selectedClauseIds.length;
        this.showDeleteConfirmDialog = true;
    }

    confirmDelete() {
        this.showDeleteConfirmDialog = false;
        this.clauseLibraryService.bulkDeleteClauses(this.selectedClauseIds).subscribe((res) => {
            if (res.success) {
                this.toast.success(`${this.itemsToDeleteCount} clauses deleted successfully`);
                this.clearSelections();
                this.skip = 0;
                this.getClauses();
                this.getClauseBundles();
            }
        }, (error) => {
            this.toast.error('Failed to delete clauses');
            this.clearSelections();
        });
    }

    cancelDelete() {
        this.showDeleteConfirmDialog = false;
        this.itemsToDeleteCount = 0;
    }

    // Bulk edit clauses
    openBulkEdit() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause to edit');
            return;
        }
        if (this.selectedClauseIds.length === 1) {
            const clause = this.selectedClauses[0];
            if (clause) this.editClause(clause);
            return;
        }
        this.editingClauses = this.selectedClauses.map(c => ({ ...c }));
        this.originalClauses = this.selectedClauses.map(c => ({ ...c }));
        this.currentEditIndex = 0;
        this.showBulkEditDialog = true;
        this.totalEditingCount = this.editingClauses.length;
        this.isBulkEditPreviousDisabled = true;
        this.isBulkEditNextDisabled = this.editingClauses.length <= 1;
        this.savedClauses.clear();
        this.isBulkEditSaveDisabled = true;
        this.bulkEditGenerateLoading = false;
        this.bulkEditRephraseLoading = false;
        this.bulkEditClauseTextExpanded = false;
    }

    openPromptForBulkEditClauseDialog() {
        const clause = this.editingClauses[this.currentEditIndex];
        if (!clause?.name?.trim()) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.isPromptForBulkEditMode = true;
        this.promptForClauseText = '';
        this.showPromptForClauseDialog = true;
    }

    generateClauseTextForBulkEdit() {
        this.openPromptForBulkEditClauseDialog();
    }

    rephraseClauseForBulkEdit() {
        const clause = this.editingClauses[this.currentEditIndex];
        const name = (clause?.name ?? '').trim();
        const text = (clause?.text ?? '').trim();
        if (!text) {
            this.toast.error('Clause text is empty');
            return;
        }
        const editIndex = this.currentEditIndex;
        this.bulkEditRephraseLoading = true;
        this.clauseLibraryService.rephraseClause(name, text).subscribe({
            next: (res) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingBulkClauseTextThreadId = res.data.threadId;
                    this.pendingBulkClauseTextEditIndex = editIndex;
                } else {
                    this.bulkEditRephraseLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text rephrase');
                }
            },
            error: () => {
                this.bulkEditRephraseLoading = false;
                this.toast.error('Failed to start clause text rephrase');
            }
        });
    }

    onBulkEditDialogHide() {
        if (this.bulkEditGenerateLoading || this.bulkEditRephraseLoading) {
            this.confirmationService.confirm({
                message: 'The clause is currently being generated. If you exit now, it will not be updated. Are you sure you want to cancel?',
                header: 'Cancel generation?',
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.pendingBulkClauseTextThreadId = null;
                    this.pendingBulkClauseTextEditIndex = null;
                    this.showBulkEditDialog = false;
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    this.onCancelBulkEdit();
                },
                reject: () => {
                    this.showBulkEditDialog = true;
                }
            });
            return;
        }
        this.showBulkEditDialog = false;
        this.onCancelBulkEdit();
    }

    selectClauseForEdit(index: number) {
        if ((this.bulkEditGenerateLoading || this.bulkEditRephraseLoading) && index !== this.currentEditIndex) {
            this.confirmationService.confirm({
                message: 'The clause is currently being generated. If you switch now, the result will not be applied. Are you sure you want to cancel?',
                header: 'Cancel generation?',
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.pendingBulkClauseTextThreadId = null;
                    this.pendingBulkClauseTextEditIndex = null;
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    this.currentEditIndex = index;
                    this.isBulkEditPreviousDisabled = index === 0;
                    this.isBulkEditNextDisabled = index === this.editingClauses.length - 1;
                },
                reject: () => { }
            });
            return;
        }
        this.currentEditIndex = index;
    }

    navigateBulkEditPrevious() {
        if (this.currentEditIndex > 0) {
            this.currentEditIndex--;
        }
        this.isBulkEditPreviousDisabled = this.currentEditIndex === 0;
        this.isBulkEditNextDisabled = this.currentEditIndex === this.editingClauses.length - 1;
    }

    navigateBulkEditNext() {
        if (this.currentEditIndex < this.editingClauses.length - 1) {
            this.currentEditIndex++;
        }
        this.isBulkEditPreviousDisabled = this.currentEditIndex === 0;
        this.isBulkEditNextDisabled = this.currentEditIndex === this.editingClauses.length - 1;
    }

    saveClauseInBulkEdit(index: number) {
        const orig = this.originalClauses[index];
        const edit = this.editingClauses[index];
        const changed = (orig.name !== edit.name) || (orig.text !== edit.text);
        if (changed) {
            this.savedClauses.add(index);
        } else {
            this.savedClauses.delete(index);
        }
        this.isBulkEditSaveDisabled = this.savedClauses.size === 0;
    }

    onCancelBulkEdit() {
        this.showBulkEditDialog = false;
        this.editingClauses = [];
        this.originalClauses = [];
        this.currentEditIndex = 0;
        this.resetBulkEditDialog();
        this.isBulkEditPreviousDisabled = true;
        this.clearSelections();
    }

    onSaveBulkEdit(checkComplete: boolean = true) {
        const modifiedClauses = Array.from(this.savedClauses).map(index => {
            const clause = this.editingClauses[index];
            return { _id: clause._id, name: clause.name ?? '', text: clause.text ?? '' };
        });
        if (checkComplete && modifiedClauses.length !== this.editingClauses.length) {
            this.saveIncompleteClauses();
            return;
        }
        if (modifiedClauses.length === 0) {
            this.toast.error('No changes to save');
            return;
        }
        this.isBulkEditSaving = true;
        this.clauseLibraryService.bulkUpdateClauses(modifiedClauses).subscribe({
            next: (res) => {
                this.isBulkEditSaving = false;
                if (res.success) {
                    this.toast.success(`${modifiedClauses.length} clauses updated successfully`);
                    this.showBulkEditDialog = false;
                    this.skip = 0;
                    this.getClauses();
                    this.getClauseBundles();
                    this.clearSelections();
                }
            }
        });
    }

    saveIncompleteClauses() {
        this.confirmationService.confirm({
            message: 'Your progress on edited clauses will be be saved. Unedited clauses will remain pending. DO you want to continue?',
            header: 'Save incomplete?',
            accept: () => this.onSaveBulkEdit(false),
            reject: () => { },
            acceptLabel: 'Save and close',
            rejectLabel: 'Keep editing',
        });
    }

    handleConfirmAccept(message: any) {
        if (message.accept) {
            message.accept();
        }
        this.confirmationService.close();
    }

    handleConfirmReject(message: any) {
        if (message.reject) {
            message.reject();
        }
        this.confirmationService.close();
    }

    resetBulkEditDialog() {
        this.totalEditingCount = 0;
        this.isBulkEditPreviousDisabled = true;
        this.isBulkEditNextDisabled = true;
        this.isBulkEditSaveDisabled = true;
        this.savedClauses.clear();
    }

    // Bulk download clauses
    bulkDownloadClauses() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause to download');
            return;
        }
        this.clauseLibraryService.bulkDownloadClauses(this.selectedClauseIds).subscribe((res) => {
            if (res.success && res.data) {
                let blob: Blob;
                let filename: string;

                blob = new Blob([new Uint8Array(res.data.data)], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
                });
                filename = 'clauses.xlsx';
                saveAs(blob, filename);
                this.toast.success(`${this.selectedClauseIds.length} clauses downloaded successfully`);
            } else {
                this.toast.error('Failed to download');
            }
        }, (error) => {
            this.toast.error('Failed to download clauses');
        });
    }

    // Bulk link to clause bundles
    openBulkLinkBundleDialog() {
        if (this.selectedClauseIds.length === 0) {
            this.toast.error('Please select at least one clause to link');
            return;
        }
        this.bulkLinkSearchValue = '';
        this.selectedBundleIdsForBulkLink = [];
        this.showLinkBundleDialog = true;
    }

    get filteredClauseBundles(): IClauseBundle[] {
        if (!this.bulkLinkSearchValue.trim()) {
            return this.clauseBundles;
        }
        const searchLower = this.bulkLinkSearchValue.toLowerCase();
        return this.clauseBundles.filter(bundle =>
            bundle.name.toLowerCase().includes(searchLower)
        );
    }

    toggleBundleSelection(bundleId: string) {
        const index = this.selectedBundleIdsForBulkLink.indexOf(bundleId);
        if (index > -1) {
            this.selectedBundleIdsForBulkLink.splice(index, 1);
        } else {
            this.selectedBundleIdsForBulkLink.push(bundleId);
        }
    }

    isBundleSelected(bundleId: string): boolean {
        return this.selectedBundleIdsForBulkLink.includes(bundleId);
    }

    submitBulkLinkToBundles() {
        if (this.selectedBundleIdsForBulkLink.length === 0) {
            this.toast.error('Please select at least one clause bundle');
            return;
        }

        this.bulkLinkingToBundles = true;

        this.clauseLibraryService.bulkLinkClausesToBundles(this.selectedClauseIds, this.selectedBundleIdsForBulkLink).subscribe((res) => {
            this.bulkLinkingToBundles = false;
            if (res.success) {
                this.toast.success(`${this.selectedClauseIds.length} clauses linked to ${this.selectedBundleIdsForBulkLink.length} bundle(s) successfully`);
                this.showLinkBundleDialog = false;
                this.clearSelections();
                this.skip = 0;
                this.getClauses();
                this.getClauseBundles();
            }
        }, (error) => {
            this.bulkLinkingToBundles = false;
            this.toast.error('Failed to link clauses to bundles');
        });
    }

    openCreateBundleInBulkLinkDialog() {
        this.createBundleFromBulkLinkForm.reset({ name: '' });
        this.showCreateBundleInBulkLinkDialog = true;
    }

    closeCreateBundleInBulkLinkDialog() {
        this.showCreateBundleInBulkLinkDialog = false;
    }

    createNewBundleFromBulkLink() {
        this.openCreateBundleInBulkLinkDialog();
    }

    submitCreateBundleFromBulkLink() {
        const name = this.createBundleFromBulkLinkForm.value.name?.trim();
        if (!name) return;
        this.createBundleFromBulkLinkLoading = true;
        this.clauseBundleService.createBundle(name).subscribe({
            next: (res) => {
                this.createBundleFromBulkLinkLoading = false;
                if (res?.success && res?.data) {
                    const newId = (res.data as { _id?: string })._id;
                    this.toast.success('Clause bundle created successfully');
                    this.showCreateBundleInBulkLinkDialog = false;
                    this.getClauseBundles();
                    this.clauseBundleRef?.loadBundles();
                    if (newId && !this.selectedBundleIdsForBulkLink.includes(newId)) {
                        this.selectedBundleIdsForBulkLink = [...this.selectedBundleIdsForBulkLink, newId];
                    }
                } else {
                    this.toast.error('Failed to create clause bundle');
                }
            },
            error: () => {
                this.createBundleFromBulkLinkLoading = false;
                this.toast.error('Failed to create clause bundle');
            },
        });
    }

    // Get users
    getUsers() {
        this.isLoadingUsers = true;
        this.userService.getWorkspaceUsers({ includeAdmins: true }).subscribe(res => {
            if (res && res.success) {
                this.users = res.data;
                this.userOptions = res.data.map(user => ({
                    label: user.fullName || `${user.firstName} ${user.lastName}` || user.email,
                    value: user._id
                }));
            }
            this.isLoadingUsers = false;
        });
    }

    // Get clause bundles (id, name) for dropdowns and filters
    getClauseBundles() {
        this.clauseBundleService.getBundles({}, false).subscribe((res: any) => {
            if (res && res.success) {
                this.clauseBundles = res.data.bundles as IClauseBundle[];
                this.clauseBundleOptions = this.clauseBundles.map(bundle => ({
                    label: bundle.name,
                    value: bundle._id
                }));
            } else {
                this.clauseBundles = [];
                this.clauseBundleOptions = [];
            }
        });
    }

    // Helper methods
    getClauseBundleName(clause: IClause): string {
        if (!clause.clauseBundles || clause.clauseBundles.length === 0) {
            return '-';
        }

        const names = clause.clauseBundles.map(bundle =>
            typeof bundle === 'string' ? bundle : (bundle.name || '-')
        );
        return names.join(', ');
    }

    getClauseBundleIds(clause: IClause): string[] {
        if (!clause.clauseBundles || clause.clauseBundles.length === 0) {
            return [];
        }
        return clause.clauseBundles.map(b => (typeof b === 'string' ? b : b._id));
    }


    getCurrentClauseBundleNames(): string {
        return (this.currentClause.clauseBundles as Partial<IClauseBundle>[])?.map(bundle => bundle.name).join(', ') || '-';
    }

    getCreatedByName(clause: IClause): string {
        if (typeof clause.createdBy === 'string') {
            return clause.createdBy;
        }
        return clause.createdBy?.fullName || clause.createdBy?.email || 'Unknown';
    }

    getSourceLabel(source: CLAUSE_SOURCE): string {
        switch (source) {
            case CLAUSE_SOURCE.MANUAL:
                return 'Manual entry';
            case CLAUSE_SOURCE.EXCEL:
                return 'Excel';
            case CLAUSE_SOURCE.EXTRACTED:
                return 'Extracted';
            default:
                return source;
        }
    }

    getStatusLabel(status: CLAUSE_STATUS): string {
        switch (status) {
            case CLAUSE_STATUS.APPROVED:
                return 'Approved';
            case CLAUSE_STATUS.REJECTED:
                return 'Rejected';
            case CLAUSE_STATUS.PENDING:
                return 'Pending';
            default:
                return status;
        }
    }


    formatDate(date: string | Date | undefined): string {
        if (!date) return '-';
        const d = new Date(date);
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: 'long' });
        const year = d.getFullYear();

        const suffix = (day: number) => {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        return `${day}${suffix(day)} ${month} ${year}`;
    }

    closeAllMenus() {
        this.mobileMenu?.hide();
        this.sortFilterPopover?.hide();
    }

    // File selection fetch function
    fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> => {
        if (event.tabId === 'workspace') {
            if (event.dropdownId === 'all') {
                return this.fetchAllKnowledgeBaseFiles(event);
            } else if (event.dropdownId) {
                return this.fetchKnowledgeBaseFiles(event.dropdownId, event);
            } else {
                this.clauseBundleDropdownOptions.set([]);
                this.prepareWorkspaceDropdownOptions();
                return of({ items: [], totalRecords: 0 });
            }
        } else if (event.tabId === 'project') {
            if (event.dropdownId === 'all') {
                return this.fetchAllProjectDocuments(event);
            } else if (event.dropdownId) {
                return this.fetchProjectDocuments(event.dropdownId, event);
            } else {
                this.clauseBundleDropdownOptions.set([]);
                this.prepareProjectDropdownOptions();
                return of({ items: [], totalRecords: 0 });
            }
        } else if (event.tabId === 'documents') {
            if (event.dropdownId === 'all') {
                return this.fetchDocuments(event);
            } else if (event.dropdownId && this.projects.some(p => p._id === event.dropdownId)) {
                return this.fetchProjectDocuments(event.dropdownId, event);
            } else {
                this.clauseBundleDropdownOptions.set([]);
                this.prepareDocumentsDropdownOptions();
                return of({ items: [], totalRecords: 0 });
            }
        }

        return of({ items: [], totalRecords: 0 });
    };

    prepareWorkspaceDropdownOptions() {
        const options: DropdownOption[] = [
            {
                id: 'all',
                label: 'All files',
                groupKey: 'metadata.kbId',
                groupLabel: 'metadata.kbName'
            }
        ];

        this.knowledgeBases.forEach(kb => {
            options.push({
                id: kb._id,
                label: kb.name,
                value: kb,
                groupKey: 'metadata.kbId',
                groupLabel: 'metadata.kbName'
            });
        });

        this.clauseBundleDropdownOptions.set(options);
    }

    prepareProjectDropdownOptions() {
        const options: DropdownOption[] = [
            {
                id: 'all',
                label: 'All projects',
                groupKey: 'metadata.projectId',
                groupLabel: 'metadata.projectName'
            }
        ];

        this.projects.forEach(project => {
            options.push({
                id: project._id,
                label: project.name,
                value: project,
                groupKey: 'metadata.projectId',
                groupLabel: 'metadata.projectName'
            });
        });

        this.clauseBundleDropdownOptions.set(options);
    }

    prepareDocumentsDropdownOptions() {
        const options: DropdownOption[] = [
            {
                id: 'all',
                label: 'All documents',
                groupKey: 'metadata.projectId',
                groupLabel: 'metadata.projectName'
            }
        ];

        this.projects.forEach(project => {
            options.push({
                id: project._id,
                label: project.name,
                value: project,
                groupKey: 'metadata.projectId',
                groupLabel: 'metadata.projectName'
            });
        });

        this.clauseBundleDropdownOptions.set(options);
    }

    private fetchAllKnowledgeBaseFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareWorkspaceDropdownOptions();

        const params = {
            scope: KNOWLEDGE_SCOPE.WORKSPACE
        };
        return this.repositoryService.getFiles(params).pipe(
            map((res) => {
                const allFiles: FileItem[] = [];
                const files = res.data;
                const filteredFiles = files.filter(file => {
                    if (file.type !== 'FILE') return false;
                    if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
                        return false;
                    }
                    return true;
                });

                filteredFiles.forEach((file: any) => {
                    const firstKb = file.knowledgeBases?.[0];
                    const kbDetails = firstKb ? this.knowledgeBases.find(k => k._id === firstKb._id) : undefined;
                    allFiles.push({
                        id: file._id,
                        name: file.name || 'Unknown',
                        size: file.size,
                        metadata: {
                            kbId: kbDetails?._id ?? "file-repository",
                            kbName: kbDetails?.name ?? "Files",
                            originalFile: file
                        }
                    });
                });

                const startIndex = event.page * event.pageSize;
                const endIndex = startIndex + event.pageSize;
                const paginatedFiles = allFiles.slice(startIndex, endIndex);

                return { items: paginatedFiles, totalRecords: allFiles.length };
            })
        );
    }

    private fetchKnowledgeBaseFiles(kbId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareWorkspaceDropdownOptions();

        const kb = this.knowledgeBases.find(k => k._id === kbId);
        if (!kb) {
            return of({ items: [], totalRecords: 0 });
        }

        let files: FileItem[] = [];
        if (kb.files && Array.isArray(kb.files)) {
            files = kb.files
                .filter((file: any) => {
                    if (event.searchQuery && !file.file?.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) {
                        return false;
                    }
                    return true;
                })
                .map((file: any) => ({
                    id: file.file?._id,
                    name: file.file?.name || 'Unknown',
                    size: file.file?.size,
                    metadata: {
                        kbId: kb._id,
                        kbName: kb.name,
                        originalFile: file.file
                    }
                }));
        }

        const startIndex = event.page * event.pageSize;
        const endIndex = startIndex + event.pageSize;
        const paginatedFiles = files.slice(startIndex, endIndex);

        return of({ items: paginatedFiles, totalRecords: files.length });
    }

    private fetchAllProjectDocuments(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareProjectDropdownOptions();

        return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT }).pipe(
            map((res) => {
                const raw: IFile[] = res?.data ?? [];
                const filtered = raw.filter((file: IFile) => {
                    if (file.type !== 'FILE') return false;
                    if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) return false;
                    return true;
                });

                const allFiles: FileItem[] = filtered.map((file: IFile) => {
                    const projectRaw = file.project;
                    const projectId = typeof projectRaw === 'object' ? projectRaw?._id : projectRaw;
                    const project = this.projects.find(p => p._id === projectId);
                    return {
                        id: file._id,
                        name: file.name || 'Unknown',
                        size: file.size,
                        metadata: {
                            projectId: project?._id ?? projectId,
                            projectName: project?.name ?? (typeof projectRaw === 'object' ? projectRaw?.name : undefined),
                            originalFile: file,
                            isProjectKnowledge: true,
                        }
                    };
                });

                const start = event.page * event.pageSize;
                const end = start + event.pageSize;
                return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
            })
        );
    }

    private fetchProjectDocuments(projectId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareProjectDropdownOptions();

        const project = this.projects.find(p => p._id === projectId);
        if (!project) {
            return of({ items: [], totalRecords: 0 });
        }

        return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
            map((res) => {
                const raw: IFile[] = res?.data ?? [];
                const filtered = raw.filter((file: IFile) => {
                    if (file.type !== 'FILE') return false;
                    if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) return false;
                    return true;
                });

                const allFiles: FileItem[] = filtered.map((file: IFile) => ({
                    id: file._id,
                    name: file.name || 'Unknown',
                    size: file.size,
                    metadata: {
                        projectId: project._id,
                        projectName: project.name,
                        originalFile: file,
                        isProjectKnowledge: true,
                    }
                }));

                const start = event.page * event.pageSize;
                const end = start + event.pageSize;
                return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
            })
        );
    }

    private fetchDocuments(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareDocumentsDropdownOptions();

        return this.contractService.getDocumentsByType("", {
            search: event.searchQuery || '',
            skip: event.page * event.pageSize,
            limit: event.pageSize,
            excludeDocs: []
        }).pipe(
            map((res) => {
                if (res && res.success) {
                    const contracts = res.data?.contracts ?? [];

                    const items: FileItem[] = contracts.map((contract: IContract) => ({
                        id: contract._id,
                        name: contract.name,
                        metadata: {
                            originalContract: contract,
                            createdBy: contract.createdBy,
                            createdAt: contract.createdAt
                        }
                    }));

                    return { items, totalRecords: res.data.total };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    onFileSelectionSubmit(event: FileSelectionSubmitEvent) {
        const hasSelected = event.selectedFiles && event.selectedFiles.length > 0;
        const hasUploaded = event.uploadedFiles && event.uploadedFiles.length > 0;
        if (!hasSelected && !hasUploaded) {
            this.toast.warn('Please select at least one file');
            return;
        }

        const selectedSources: { docModel: 'Contract' | 'File'; itemId: string; attachmentId?: string }[] = [];
        if (event.selectedFiles) {
            for (const group of event.selectedFiles) {
                for (const file of group.files) {
                    if (file?.id) {
                        const isFile = !!file.metadata?.originalFile;
                        const docModel = isFile ? 'File' as const : 'Contract' as const;
                        const attachmentId = isFile
                            ? file.metadata?.originalFile?.attachment?._id ?? file.metadata?.originalFile?.attachment
                            : file.metadata?.originalDoc?.attachment?._id ?? file.metadata?.originalDoc?.attachment
                            ?? file.metadata?.originalContract?.attachment?._id ?? file.metadata?.originalContract?.attachment;
                        selectedSources.push({
                            docModel,
                            itemId: file.id,
                            ...(attachmentId && { attachmentId: typeof attachmentId === 'string' ? attachmentId : (attachmentId as any)?.toString?.() })
                        });
                    }
                }
            }
        }

        const formData = new FormData();
        formData.append('bundleIds', JSON.stringify(this.uploadContractSelectedBundleIds));
        formData.append('selectedSources', JSON.stringify(selectedSources));
        const uploadedFiles: File[] = event.uploadedFiles || [];
        uploadedFiles.forEach((file) => {
            formData.append('files', file, file.name);
        });

        this.clauseLibraryService.submitClauseExtraction(formData).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toast.success(
                        res.data?.count
                            ? `${res.data.count} item(s) queued for clause extraction. Processing in background.`
                            : 'Extraction queued successfully. Processing in background.'
                    );
                    this.fileSelectionVisible.set(false);
                    this.closeAddClauseDialog();
                    this.uploadContractSelectedBundleIds = [];
                    this.skip = 0;
                    this.getClauses();
                    this.getClauseBundles();
                    this.showClauseExtractionDialog = true;
                    this.loadClauseExtractionAnimation();
                } else {
                    this.toast.error(res.message || 'Failed to submit extraction request');
                }
            },
            error: () => {
                this.toast.error('Failed to submit extraction request');
            },
        });
    }

    loadClauseExtractionAnimation() {
        fetch('/animations/template_generation.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                this.clauseExtractionAnimationData = data;
            })
            .catch(error => {
                console.error('Error loading clause extraction animation:', error);
            });
    }

    onCloseClauseExtractionDialog() {
        this.showClauseExtractionDialog = false;
        this.clauseExtractionAnimationData = null;
    }

    onFileSelectionCancel() {
        this.fileSelectionVisible.set(false);
        this.uploadContractSelectedBundleIds = [];
    }


    // Review clauses
    getUploadedFilesAudit(open: boolean = false) {
        if (this.uploadedFilesAudit.length === 0) {
            const payload = { limit: 50, skip: 0 };
            this.clauseLibraryService.getUploadedFilesAudit(payload).subscribe((res) => {
                if (res.success && res.data) {
                    this.uploadedFilesAudit = res.data;
                    this.openHistorySidebar = open;
                }
            });
        } else {
            this.openHistorySidebar = open;
        }
    }

    openHistoryFilePreview(file: IClauseMetadataAuditItem) {
        if (!file.attachment?._id) {
            this.toast.error('File not available for preview');
            return;
        }
        this.isLoadingHistoryPreview = true;
        this.attachmentService.getAttachmentWithSignedUrl(file.attachment._id).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.selectedHistoryFileAttachment = res.data;
                    this.selectedHistoryDocumentId = res.data._id;
                    this.showHistoryFilePreview = true;
                }
                this.isLoadingHistoryPreview = false;
            },
            error: () => {
                this.toast.error('Failed to load file preview');
                this.isLoadingHistoryPreview = false;
            }
        });
    }

    closeHistoryFilePreview() {
        this.showHistoryFilePreview = false;
        this.selectedHistoryFileAttachment = null;
        this.selectedHistoryDocumentId = null;
    }

    openReviewClauses() {
        this.router.navigate(['settings/library/clause-library/review-tabs']);
    }

    isChildRouteActive(): boolean {
        return this.router.url.includes('review-tabs');
    }
}


